import type { ShapeBadge } from "../types";
import {
  MARKER_PIN_HEIGHT_PX,
  MARKER_PIN_WIDTH_PX,
  markerPinPath,
} from "./markerPin";
import type { PixelPoint } from "./snapping";

export interface BadgeContent {
  shapeId: string;
  style: NonNullable<ShapeBadge["style"]>;
  anchor: NonNullable<ShapeBadge["anchor"]>;
  imageSrc: string | null;
  text: string | null;
  /** Le texte n'apparaît qu'au survol : il ne compte pas dans l'emprise du marqueur. */
  textOnHover: boolean;
  textPosition: NonNullable<ShapeBadge["textPosition"]>;
  color: string;
  hoverColor: string;
  size: NonNullable<ShapeBadge["size"]>;
  shape: NonNullable<ShapeBadge["shape"]>;
  /** Le marqueur d'un `shapeKind: "marker"` : affiché même quand il en chevauche d'autres. */
  always?: boolean;
}

export interface BadgeBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export const PIN_SCALE = 1.4;
/** Épingle à tête carrée, dans la même boîte que `markerPinPath` et la pointe au même endroit. */
const SQUARE_PIN_PATH = "M6 1 H20 A5 5 0 0 1 25 6 V20 A5 5 0 0 1 20 25 H17 L13 36 L9 25 H6 A5 5 0 0 1 1 20 V6 A5 5 0 0 1 6 1 Z";
const PIN_WIDTH_PX = MARKER_PIN_WIDTH_PX * PIN_SCALE;
const PIN_HEIGHT_PX = MARKER_PIN_HEIGHT_PX * PIN_SCALE;
const DISC_PX = 36;
const TAG_HEIGHT_PX = 32;
const TAG_ICON_PX = 26;
const LABEL_HEIGHT_PX = 20;
const GAP_PX = 3;
const CLICK_TOLERANCE_PX = 3;
const SIZE_FACTORS: Record<BadgeContent["size"], number> = { small: 0.75, medium: 1, large: 1.35 };
const TOP_OFFSET_PX = 6;
const CHAR_WIDTH_PX = 7;
const LABEL_PADDING_PX = 16;
const MAX_COVERED_SHARE = 0.25;
const GRID_CELL_PX = 64;
const HIDDEN_ELEMENTS_KEPT = 150;

/** Le point où s'accroche le marqueur : le centre de la forme, ou le haut de sa silhouette à l'écran. */
export function badgeAnchorPx(ringPx: PixelPoint[], centerPx: PixelPoint, anchor: BadgeContent["anchor"]): PixelPoint {
  if (anchor === "center" || !ringPx.length) return centerPx;
  return { x: centerPx.x, y: Math.min(...ringPx.map((point) => point.y)) };
}

export function badgeContentKey(content: BadgeContent): string {
  return [content.style, content.anchor, content.imageSrc ?? "", content.text ?? "", content.textOnHover, content.textPosition, content.color, content.hoverColor, content.size, content.shape].join("|");
}

export function estimatedLabelWidth(text: string): number {
  return text.length * CHAR_WIDTH_PX + LABEL_PADDING_PX;
}

/** L'emprise à l'écran d'un marqueur posé en `point`, avec son texte (`labelWidth`) ou sans (`null`). */
export function badgeBox(content: Pick<BadgeContent, "style" | "anchor" | "imageSrc" | "size"> & Partial<Pick<BadgeContent, "textPosition">>, point: PixelPoint, labelWidth: number | null): BadgeBox {
  const label = labelWidth ?? 0;
  const factor = SIZE_FACTORS[content.size];
  let width: number;
  let height: number;

  if (content.style === "tag") {
    const icon = TAG_ICON_PX * factor;
    width = (content.imageSrc ? icon + GAP_PX * 2 : 0) + (labelWidth === null ? (content.imageSrc ? 0 : DISC_PX * factor) : label) + 10;
    height = Math.max(TAG_HEIGHT_PX, icon + 6);
  } else if (content.style === "pin") {
    width = Math.max(PIN_WIDTH_PX * factor, label);
    const pinHeight = PIN_HEIGHT_PX * factor;
    const labelHeight = labelWidth === null ? 0 : LABEL_HEIGHT_PX + GAP_PX;
    const below = content.textPosition === "below" ? labelHeight : 0;
    return { left: point.x - width / 2, right: point.x + width / 2, top: point.y - pinHeight - labelHeight + below, bottom: point.y + below };
  } else {
    width = Math.max(DISC_PX * factor, label);
    height = DISC_PX * factor + (labelWidth === null ? 0 : LABEL_HEIGHT_PX + GAP_PX);
  }

  if (content.anchor === "top") {
    const bottom = point.y - TOP_OFFSET_PX;
    return { left: point.x - width / 2, right: point.x + width / 2, top: bottom - height, bottom };
  }
  return { left: point.x - width / 2, right: point.x + width / 2, top: point.y - height / 2, bottom: point.y + height / 2 };
}

function area(box: BadgeBox): number {
  return Math.max(0, box.right - box.left) * Math.max(0, box.bottom - box.top);
}

function overlapArea(a: BadgeBox, b: BadgeBox): number {
  const width = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const height = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return width > 0 && height > 0 ? width * height : 0;
}

interface PlacedBox {
  box: BadgeBox;
  covered: number;
}

/**
 * Les emprises déjà occupées à l'écran, rangées par cases pour ne comparer qu'aux voisines. Un
 * marqueur peut en mordre un autre, tant qu'aucun des deux ne perd plus de `MAX_COVERED_SHARE` de
 * sa surface, tous voisins confondus.
 */
export class CollisionGrid {
  private readonly cells = new Map<string, PlacedBox[]>();

  fits(box: BadgeBox): boolean {
    const limit = area(box) * MAX_COVERED_SHARE;
    let covered = 0;
    for (const [other, overlap] of this.overlapping(box)) {
      covered += overlap;
      if (covered > limit || other.covered + overlap > area(other.box) * MAX_COVERED_SHARE) return false;
    }
    return true;
  }

  add(box: BadgeBox): void {
    const placed: PlacedBox = { box, covered: 0 };
    for (const [other, overlap] of this.overlapping(box)) {
      other.covered += overlap;
      placed.covered += overlap;
    }
    for (const key of this.keys(box)) {
      const cell = this.cells.get(key);
      if (cell) cell.push(placed);
      else this.cells.set(key, [placed]);
    }
  }

  private overlapping(box: BadgeBox): Map<PlacedBox, number> {
    const found = new Map<PlacedBox, number>();
    for (const key of this.keys(box)) {
      for (const other of this.cells.get(key) ?? []) {
        if (found.has(other)) continue;
        const overlap = overlapArea(box, other.box);
        if (overlap > 0) found.set(other, overlap);
      }
    }
    return found;
  }

  private keys(box: BadgeBox): string[] {
    const keys: string[] = [];
    for (let x = Math.floor(box.left / GRID_CELL_PX); x <= Math.floor(box.right / GRID_CELL_PX); x++) {
      for (let y = Math.floor(box.top / GRID_CELL_PX); y <= Math.floor(box.bottom / GRID_CELL_PX); y++) keys.push(`${x}:${y}`);
    }
    return keys;
  }
}

function labelWidthOf(content: BadgeContent, mounted?: MountedBadge): number | null {
  return content.text && !content.textOnHover ? (mounted?.labelWidth ?? estimatedLabelWidth(content.text)) : null;
}

interface MountedBadge {
  element: HTMLElement;
  key: string;
  labelWidth?: number;
  shownAt: number;
  visible: boolean;
  compact: boolean;
  hovered: boolean;
  highlighted: boolean;
  dimmed: boolean;
  transform: string;
  color: string;
  baseColor: string;
  hoverColor: string;
}

/**
 * Les marqueurs des formes (voir `Shape.badge`), posés au-dessus du canvas. À chaque rendu, ils sont
 * placés par ordre de priorité : un marqueur qui en recouvrirait un autre perd d'abord son texte,
 * puis disparaît. Un élément n'est créé qu'à sa première apparition.
 */
export class ShapeBadgeLayer {
  private contents: BadgeContent[] = [];
  private readonly mounted = new Map<string, MountedBadge>();
  private frame = 0;

  constructor(
    private readonly container: HTMLElement,
    private readonly onClick: (shapeId: string, event: MouseEvent) => void,
    private readonly onHover: (shapeId: string | null, event: PointerEvent) => void = () => {},
  ) {}

  get size(): number {
    return this.contents.length;
  }

  /** `contents` du plus prioritaire au moins prioritaire. */
  setBadges(contents: BadgeContent[]): void {
    this.contents = contents;
    const byId = new Map(contents.map((content) => [content.shapeId, content]));
    for (const [shapeId, badge] of this.mounted) {
      const content = byId.get(shapeId);
      if (content && badgeContentKey(content) === badge.key) continue;
      badge.element.remove();
      this.mounted.delete(shapeId);
    }
  }

  /**
   * Pendant un mouvement de la carte (`moving`), aucun marqueur affiché ne s'efface, sauf en sortant
   * de l'écran : les autres n'apparaissent que dans la place restée libre. Le tri par priorité, qui
   * peut faire céder un marqueur à un autre, attend l'arrêt — sans quoi les marqueurs voisins
   * clignoteraient à chaque image d'un zoom.
   */
  place(locate: (shapeId: string) => PixelPoint | null, moving = false): void {
    this.frame++;
    const grid = new CollisionGrid();
    const shown = new Set<string>();
    const created: MountedBadge[] = [];

    const show = (content: BadgeContent, point: PixelPoint, compact: boolean) => {
      const mounted = this.mounted.get(content.shapeId);
      const badge = mounted ?? this.mount(content);
      if (!mounted) created.push(badge);
      badge.shownAt = this.frame;
      if (!badge.visible) {
        badge.element.hidden = false;
        badge.visible = true;
      }
      if (badge.compact !== compact) {
        badge.element.classList.toggle("toka-badge--compact", compact);
        badge.compact = compact;
      }
      const transform = `translate(${Math.round(point.x)}px, ${Math.round(point.y)}px)`;
      if (badge.transform !== transform) {
        badge.element.style.transform = transform;
        badge.transform = transform;
      }
      shown.add(content.shapeId);
    };

    if (moving) {
      for (const content of this.contents) {
        const badge = this.mounted.get(content.shapeId);
        if (!badge?.visible) continue;
        const point = locate(content.shapeId);
        if (!point) continue;
        grid.add(badgeBox(content, point, badge.compact ? null : labelWidthOf(content, badge)));
        show(content, point, badge.compact);
      }
    }

    for (const content of this.contents) {
      if (shown.has(content.shapeId)) continue;
      const point = locate(content.shapeId);
      if (!point) continue;

      const labelWidth = labelWidthOf(content, this.mounted.get(content.shapeId));
      let compact = false;
      let box = badgeBox(content, point, labelWidth);
      if (!content.always && !grid.fits(box)) {
        if (labelWidth === null || (content.style === "tag" && !content.imageSrc)) continue;
        box = badgeBox(content, point, null);
        if (!grid.fits(box)) continue;
        compact = true;
      }
      grid.add(box);
      show(content, point, compact);
    }

    for (const [shapeId, badge] of this.mounted) {
      if (shown.has(shapeId) || !badge.visible) continue;
      badge.element.hidden = true;
      badge.visible = false;
    }
    this.releaseHidden(shown);

    // Mesurés après coup, en une seule passe : lire une largeur juste après chaque création forcerait
    // une mise en page par marqueur.
    for (const badge of created) {
      const width = badge.element.querySelector<HTMLElement>(".toka-badge__label")?.offsetWidth;
      if (width) badge.labelWidth = width;
    }
  }

  /**
   * L'état des marqueurs affichés : au survol, leur couleur, quand leur forme change de fond, et leur
   * grossissement ; estompés (`isDimmed`), ceux que leur zoom d'apparition masquerait hors édition.
   */
  paint(
    isHovered: (shapeId: string) => boolean,
    isDimmed: (shapeId: string) => boolean = () => false,
    isHighlighted: (shapeId: string) => boolean = () => false,
  ): void {
    for (const [shapeId, badge] of this.mounted) {
      if (!badge.visible) continue;
      const dimmed = isDimmed(shapeId);
      if (badge.dimmed !== dimmed) {
        badge.element.classList.toggle("toka-badge--under-zoom", dimmed);
        badge.dimmed = dimmed;
      }
      const hovered = isHovered(shapeId);
      if (badge.hovered !== hovered) {
        badge.element.classList.toggle("toka-badge--hovered", hovered);
        badge.hovered = hovered;
      }
      const highlighted = isHighlighted(shapeId);
      if (badge.highlighted !== highlighted) {
        badge.element.classList.toggle("toka-badge--highlighted", highlighted);
        badge.highlighted = highlighted;
      }
      const color = hovered ? badge.hoverColor : badge.baseColor;
      if (badge.color === color) continue;
      badge.element.style.setProperty("--toka-badge-color", color);
      badge.color = color;
    }
  }

  clear(): void {
    for (const { element } of this.mounted.values()) element.remove();
    this.mounted.clear();
    this.contents = [];
  }

  private releaseHidden(shown: Set<string>): void {
    const hidden = [...this.mounted].filter(([shapeId]) => !shown.has(shapeId));
    if (hidden.length <= HIDDEN_ELEMENTS_KEPT) return;
    hidden
      .sort(([, a], [, b]) => a.shownAt - b.shownAt)
      .slice(0, hidden.length - HIDDEN_ELEMENTS_KEPT)
      .forEach(([shapeId, badge]) => {
        badge.element.remove();
        this.mounted.delete(shapeId);
      });
  }

  private mount(content: BadgeContent): MountedBadge {
    const element = this.createElement(content);
    this.container.append(element);
    const badge: MountedBadge = { element, key: badgeContentKey(content), shownAt: this.frame, visible: true, compact: false, hovered: false, highlighted: false, dimmed: false, transform: "", color: content.color, baseColor: content.color, hoverColor: content.hoverColor };
    this.mounted.set(content.shapeId, badge);
    return badge;
  }

  private createElement(content: BadgeContent): HTMLElement {
    const element = document.createElement("div");
    element.className = `toka-badge toka-badge--${content.style} toka-badge--${content.anchor} toka-badge--${content.shape} toka-badge--text-${content.textPosition}`;
    if (content.textOnHover) element.classList.add("toka-badge--text-hover");
    element.dataset.shapeId = content.shapeId;
    element.setAttribute("role", "button");
    element.style.setProperty("--toka-badge-color", content.color);

    const body = document.createElement("div");
    body.className = "toka-badge__body";
    element.append(body);

    const label = content.text ? labelElement(content.text) : null;
    const factor = SIZE_FACTORS[content.size];
    const above = content.textPosition === "above" && content.style !== "tag";
    if (label && above) body.append(label);
    if (content.style === "pin") {
      body.append(pinElement(content.imageSrc, factor, content.shape));
    } else {
      const side = Math.round((content.style === "tag" ? TAG_ICON_PX : DISC_PX) * factor);
      if (content.imageSrc || content.style === "disc") body.append(discElement(content.imageSrc, side));
    }
    if (label && !above) body.append(label);

    // L'appui et le relâchement remontent jusqu'à la carte : un glisser ou un pincement commencé sur
    // un marqueur doit déplacer ou zoomer la carte comme ailleurs. Seul le clic, sans déplacement,
    // appartient au marqueur.
    for (const type of ["dblclick", "contextmenu"]) {
      element.addEventListener(type, (event) => event.stopPropagation());
    }
    let pressedAt: PixelPoint | null = null;
    element.addEventListener("pointerdown", (event) => {
      pressedAt = { x: event.clientX, y: event.clientY };
    });
    element.addEventListener("click", (event) => {
      event.stopPropagation();
      const moved = pressedAt != null && Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y) > CLICK_TOLERANCE_PX;
      pressedAt = null;
      if (!moved) this.onClick(content.shapeId, event);
    });
    element.addEventListener("pointerenter", (event) => this.onHover(content.shapeId, event));
    element.addEventListener("pointerleave", (event) => this.onHover(null, event));
    return element;
  }
}

function labelElement(text: string): HTMLElement {
  const label = document.createElement("span");
  label.className = "toka-badge__label";
  label.textContent = text;
  return label;
}

function discElement(imageSrc: string | null, side: number): HTMLElement {
  const disc = document.createElement("span");
  disc.className = "toka-badge__disc";
  disc.style.width = `${side}px`;
  disc.style.height = `${side}px`;
  if (imageSrc) disc.append(imageElement(imageSrc));
  return disc;
}

function pinElement(imageSrc: string | null, factor: number, shape: BadgeContent["shape"]): HTMLElement {
  const pin = document.createElement("span");
  pin.className = "toka-badge__pin";
  pin.style.width = `${Math.round(PIN_WIDTH_PX * factor)}px`;
  pin.style.height = `${Math.round(PIN_HEIGHT_PX * factor)}px`;

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `0 0 ${MARKER_PIN_WIDTH_PX} ${MARKER_PIN_HEIGHT_PX}`);
  svg.setAttribute("width", String(Math.round(PIN_WIDTH_PX * factor)));
  svg.setAttribute("height", String(Math.round(PIN_HEIGHT_PX * factor)));
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", shape === "square" ? SQUARE_PIN_PATH : markerPinPath());
  svg.append(path);
  pin.append(svg);

  if (imageSrc) {
    const image = imageElement(imageSrc);
    image.className = "toka-badge__pin-image";
    pin.append(image);
  }
  return pin;
}

/**
 * Chargée en CORS, comme MapLibre charge la même adresse pour une image posée sur la carte : un
 * chargement sans CORS empoisonnerait le cache d'un stockage qui ne renvoie pas `Vary: Origin`, et
 * l'image ne pourrait plus y être posée. Un hôte sans CORS retombe sur un chargement ordinaire.
 */
function imageElement(src: string): HTMLImageElement {
  const image = document.createElement("img");
  image.crossOrigin = "anonymous";
  image.addEventListener("error", () => {
    image.removeAttribute("crossorigin");
    image.src = src;
  }, { once: true });
  image.src = src;
  image.alt = "";
  image.draggable = false;
  image.decoding = "async";
  return image;
}
